/**
 * NodeContextMenu Component
 *
 * Provides a context menu for React Flow nodes with conversion options.
 *   - user_prompt -> auto_prompt (existing)
 *   - conditional -> filter (AC-1289, Epic AC-1285)
 *
 * Props:
 * - node: The React Flow node that was right-clicked
 * - position: The x,y position where the menu should appear
 * - onConvert: Async callback for user_prompt -> auto_prompt conversion
 * - onConvertToFilter: Async callback for conditional -> filter conversion
 * - onClose: Callback to close the context menu
 */

import React, { useState, useEffect } from 'react';
import { Node } from 'reactflow';
import toast from 'react-hot-toast';
import { WorkflowStep } from '../../types';
import apiService from '../../services/api';

interface FilterConversionPreview {
  convertible: boolean;
  reason?: string;
  preview?: {
    source_field: string;
    output_field: string;
    conditions: string[];
    on_empty: 'continue' | 'exit';
    element_class_name?: string | null;
  };
}

interface NodeContextMenuProps {
  node: Node;
  position: { x: number; y: number };
  /** user_prompt -> auto_prompt conversion */
  onConvert: (stepId: string) => Promise<void>;
  /** AC-1289: conditional -> filter conversion */
  onConvertToFilter?: (stepId: string) => Promise<void>;
  onClose: () => void;
}

export const NodeContextMenu: React.FC<NodeContextMenuProps> = ({
  node,
  position,
  onConvert,
  onConvertToFilter,
  onClose,
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);

  // AC-1289: convert-to-filter preview state
  const [filterPreview, setFilterPreview] = useState<FilterConversionPreview | null>(null);
  const [filterPreviewLoading, setFilterPreviewLoading] = useState(false);

  // Clear error state when component unmounts or closes
  useEffect(() => {
    if (!showConfirmDialog && !showFilterDialog) {
      setError(null);
    }
  }, [showConfirmDialog, showFilterDialog]);

  // Extract step data from the node
  const step = node.data?.step as WorkflowStep | undefined;

  const isConditional = !!step && step.step_type === 'conditional';
  const isUserPrompt = !!step && step.step_type === 'user_prompt';

  // AC-1289: On mount for conditional nodes, fetch filter-conversion preview.
  // The result gates whether the menu entry is enabled and controls the tooltip
  // reason shown for non-convertible steps.
  useEffect(() => {
    let cancelled = false;

    if (!isConditional) {
      setFilterPreview(null);
      return () => {
        cancelled = true;
      };
    }

    const workflowId = (step as any)?.orchestration_id;
    const stepId = (step as any)?.orchestration_step_id;
    if (workflowId == null || stepId == null) {
      return () => {
        cancelled = true;
      };
    }

    setFilterPreviewLoading(true);
    apiService
      .previewFilterConversion(workflowId, stepId)
      .then((res) => {
        if (!cancelled) {
          setFilterPreview(res);
          setFilterPreviewLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setFilterPreview({
            convertible: false,
            reason: err?.response?.data?.detail || 'Preview unavailable',
          });
          setFilterPreviewLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // Only refetch when the step identity changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConditional, (step as any)?.orchestration_id, (step as any)?.orchestration_step_id]);

  // Only render menu for supported step types.
  if (!step || (!isUserPrompt && !isConditional)) {
    return null;
  }

  // ---- Auto-Prompt handlers ----

  const handleConvertClick = () => {
    setShowConfirmDialog(true);
    setError(null);
  };

  const handleConfirm = async () => {
    if (!step.orchestration_step_id) {
      setError('Invalid step: missing step ID');
      return;
    }

    setIsConverting(true);
    setError(null);

    try {
      await onConvert(step.orchestration_step_id.toString());
      toast.success('Step converted to Auto-Prompt successfully');
      setShowConfirmDialog(false);
      onClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Conversion failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsConverting(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
    setError(null); // Clear error state when menu closes
    onClose();
  };

  // ---- AC-1289: Convert-to-Filter handlers ----

  const filterConvertible = !!filterPreview && filterPreview.convertible === true;
  const filterDisabledReason = filterPreview && !filterPreview.convertible
    ? (filterPreview.reason || 'This conditional cannot be converted to a filter')
    : undefined;

  const handleFilterConvertClick = () => {
    if (!filterConvertible) return;
    setShowFilterDialog(true);
    setError(null);
  };

  const handleFilterConfirm = async () => {
    if (!step.orchestration_step_id) {
      setError('Invalid step: missing step ID');
      return;
    }

    setIsConverting(true);
    setError(null);

    try {
      const stepIdStr = step.orchestration_step_id.toString();
      if (onConvertToFilter) {
        await onConvertToFilter(stepIdStr);
      } else {
        const workflowId = (step as any).orchestration_id;
        await apiService.convertConditionalToFilter(workflowId, stepIdStr);
      }
      toast.success('Step converted to Filter successfully');
      setShowFilterDialog(false);
      onClose();
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.detail || err?.message || 'Conversion failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsConverting(false);
    }
  };

  const handleFilterCancel = () => {
    setShowFilterDialog(false);
    setError(null);
    onClose();
  };

  const preview = filterPreview?.preview;
  const originalCondition =
    (step as any)?.condition?.conditions?.[0] ?? '(no condition)';
  const rewrittenCondition =
    (preview?.conditions && preview.conditions[0]) || '(preview unavailable)';

  return (
    <div
      role="menu"
      className="absolute bg-white dark:bg-cap-dark rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      {isUserPrompt && (
        <button
          onClick={handleConvertClick}
          className="block w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:text-warm-white rounded-lg"
        >
          Convert to Auto-Prompt
        </button>
      )}

      {/* When the filter-conversion dialog is open, hide the menu entry to
          keep the confirm surface unambiguous. The auto-prompt flow (above)
          intentionally keeps its menu entry visible so existing regression
          tests continue to find both the menu button and the dialog button. */}
      {isConditional && !showFilterDialog && (
        <button
          onClick={handleFilterConvertClick}
          disabled={filterPreview ? !filterConvertible : false}
          aria-disabled={filterPreview ? !filterConvertible : false}
          title={filterDisabledReason}
          className="block w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:text-warm-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Convert to Filter
        </button>
      )}

      {showConfirmDialog && (
        <div
          role="dialog"
          className="p-4 min-w-[200px] sm:min-w-[300px]"
        >
          <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-warm-white">Convert to Auto-Prompt</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            This will convert the user prompt step to an auto-prompt step that
            automatically generates responses.
          </p>

          {error && (
            <div className="text-red-600 dark:text-red-400 text-sm mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
              {error}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button
              onClick={handleConfirm}
              disabled={isConverting}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-primary-600 disabled:bg-blue-300 disabled:cursor-not-allowed"
            >
              {isConverting ? 'Converting...' : 'Convert'}
            </button>
            <button
              onClick={handleCancel}
              disabled={isConverting}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showFilterDialog && (
        <div
          role="dialog"
          className="p-4 min-w-[240px] sm:min-w-[380px]"
          data-testid="convert-to-filter-dialog"
        >
          <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-warm-white">Convert to Filter</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            This will convert the conditional step into a filter step that
            iterates the list per element.
          </p>

          <div className="text-xs space-y-2 mb-3">
            <div>
              <div className="font-medium text-gray-700 dark:text-gray-200">Original condition</div>
              <div className="font-mono text-gray-600 dark:text-gray-400 break-all">
                {originalCondition}
              </div>
            </div>
            <div>
              <div className="font-medium text-gray-700 dark:text-gray-200">Rewritten condition</div>
              <div className="font-mono text-gray-600 dark:text-gray-400 break-all">
                {rewrittenCondition}
              </div>
            </div>
          </div>

          <div className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-2 py-2 mb-3">
            Non-matching elements will be dropped and only matching ones flow downstream.
          </div>

          {error && (
            <div className="text-red-600 dark:text-red-400 text-sm mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
              {error}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button
              onClick={handleFilterConfirm}
              disabled={isConverting}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-primary-600 disabled:bg-blue-300 disabled:cursor-not-allowed"
            >
              {isConverting ? 'Converting...' : 'Confirm'}
            </button>
            <button
              onClick={handleFilterCancel}
              disabled={isConverting}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NodeContextMenu;
